<template>
  <div class="admin-noticeboard p-4 bg-white rounded-lg shadow-md relative flex flex-col gap-4 h-full">
    <div class="justify-between flex border-b-2 pb-2 items-center">
      <span class="text-lg font-bold text-primary">
        My Tasks
      </span>
      <Button
        label="View All"
        size="small"
        outlined
        @click="navigateTo('/tasks')"
      />
    </div>

    <div
      v-if="!isLoading"
      class="overflow-y-auto show-y-thin-scrollbar px-4 flex-auto"
    >
      <div
        v-if="tasks.length === 0"
        class="flex items-center justify-center h-full"
      >
        <p class="text-gray-500 text-center">
          No tasks assigned to you
        </p>
      </div>
      <div
        v-else
        class="flex-auto space-y-3"
      >
        <div
          v-for="task in tasks"
          :key="task.id"
          class="flex flex-col gap-2 p-3 border rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
          @click="navigateTo('/tasks')"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <Tag
                  :class="task.isCompleted ? '!bg-green-100 !text-green-800' : '!bg-red-100 !text-red-800'"
                  class="w-fit"
                >
                  <div class="flex font-medium text-xs items-center gap-1">
                    <Icon
                      :name="task.isCompleted ? 'mdi:checkbox-marked-outline' : 'mdi:checkbox-blank-outline'"
                      size="14"
                    />
                    <span>{{ task.isCompleted ? 'Completed' : 'Pending' }}</span>
                  </div>
                </Tag>
                <span
                  class="text-xs flex items-center gap-1"
                  :class="getDueDateClass(task)"
                >
                  <Icon
                    name="mdi:bell-outline"
                    size="14"
                  />
                  {{ formatDueDate(task.dueDate) }}
                </span>
              </div>
              <p class="text-sm text-gray-800 font-medium line-clamp-2">
                {{ task.description }}
              </p>
            </div>
            <Button
              :icon="task.isCompleted ? 'pi pi-undo' : 'pi pi-check'"
              :severity="task.isCompleted ? 'secondary' : 'success'"
              size="small"
              variant="text"
              @click.stop="toggleTaskStatus(task)"
            />
          </div>
          <div
            v-if="task.business || task.contact"
            class="flex items-center gap-3 text-xs text-gray-500"
          >
            <div
              v-if="task.business"
              class="flex items-center gap-1"
            >
              <Icon
                name="mdi:company"
                size="14"
              />
              <span>{{ task.business.name }}</span>
            </div>
            <div
              v-if="task.contact"
              class="flex items-center gap-1"
            >
              <Icon
                name="mdi:contact"
                size="14"
              />
              <span>{{ task.contact.firstName }} {{ task.contact.lastName }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex items-center justify-center h-full"
    >
      <ProgressSpinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useTasks } from '~/composables/useTask'
import { useUtils } from '~/composables/useUtils'
import { useToast } from 'primevue/usetoast'

const { getAllTasksByUser, updateTaskStatus } = useTasks()
const { formatSupabaseTimeStamp, getCurrentDateTime, TimeStampFormat } = useUtils()
const toast = useToast()

const isLoading = ref(true)
const allTasks = ref([])

const tasks = computed(() => {
  return allTasks.value.filter((task: any) => {
    return task.isCompleted !== true
  })
})

const init = async () => {
  isLoading.value = true
  try {
    const res = await getAllTasksByUser()
    if (res?.data) {
      const today = getCurrentDateTime(TimeStampFormat.DayMonthYear)
      allTasks.value = res.data
        .slice(0, 10)
        .map((task: any) => {
          const formattedDueDate = formatSupabaseTimeStamp(
            task.dueDate,
            TimeStampFormat.DayMonthYear,
          )
          return {
            ...task,
            dueDate: formattedDueDate,
            dueToday: today && formattedDueDate.sorting === today.sorting,
            overdue: today && !task.isCompleted && formattedDueDate.sorting < today.sorting,
            upcoming: today && formattedDueDate.sorting > today.sorting,
          }
        })
    }
  }
  catch (error) {
    console.error('Error fetching tasks:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch tasks',
      life: 3000,
    })
  }
  finally {
    isLoading.value = false
  }
}

const formatDueDate = (dueDate: any) => {
  if (!dueDate?.formatted) return 'No due date'
  return `Due: ${dueDate.formatted}`
}

const getDueDateClass = (task: any) => {
  if (task.isCompleted) return 'text-gray-400'
  if (task.overdue) return 'text-red-500 font-semibold'
  if (task.dueToday) return 'text-orange-500 font-semibold'
  return 'text-gray-600'
}

const toggleTaskStatus = async (task: any) => {
  try {
    const { error } = await updateTaskStatus(task.id, !task.isCompleted)
    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update task status',
        life: 3000,
      })
      return
    }
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Task marked as ${!task.isCompleted ? 'completed' : 'pending'}`,
      life: 3000,
    })
    // Update local state - the computed property will automatically filter it out
    task.isCompleted = !task.isCompleted
    // Force reactivity update
    allTasks.value = [...allTasks.value]
  }
  catch (error) {
    console.error('Error updating task status:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update task status',
      life: 3000,
    })
  }
}

onMounted(async () => {
  await init()
})
</script>
